import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Stack } from "@mui/material";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import RecommendRoundedIcon from "@mui/icons-material/RecommendRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import CourseApi, { CourseSummary } from "../api/CourseApi";
import SelfTestApi, { SelfTestSummary } from "../api/SelfTestApi";
import AssignmentApi, { Assignment } from "../api/AssignmentApi";
import { useAuth } from "../context/AuthContext";

export default function Courses() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [courses, setCourses] = useState<CourseSummary[]>([]);
  const [selftests, setSelftests] = useState<SelfTestSummary[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  useEffect(() => {
    CourseApi.list().then(setCourses).catch(() => setCourses([]));
    SelfTestApi.list().then(setSelftests).catch(() => setSelftests([]));
  }, []);
  useEffect(() => {
    if (user) AssignmentApi.list().then(setAssignments).catch(() => setAssignments([]));
    else setAssignments([]);
  }, [user]);

  // Verdiepende trajecten en reflectie-vragenlijsten (visibility
  // "assignable") staan in aparte secties: alleen naam + korte beschrijving.
  // De inhoud is pas toegankelijk na toewijzing door een psycholoog
  // (API-afgedwongen); toegewezen items zijn wél klikbaar.
  const openCourses = courses.filter((c) => (c.visibility || "open") === "open");
  const assignableCourses = courses.filter((c) => c.visibility === "assignable");
  const openTests = selftests.filter((s) => (s.visibility || "open") === "open");
  const assignableTests = selftests.filter((s) => s.visibility === "assignable");
  const assignedSlugs = new Set(assignments.filter((a) => a.kind !== "selftest").map((a) => a.courseSlug));
  const assignedTestSlugs = new Set(assignments.filter((a) => a.kind === "selftest").map((a) => a.selfTestSlug));

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 5, sm: 8 } }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "2.25rem", sm: "3rem" }, mb: 1.5 }}>{t("courses.title")}</Typography>
        <Typography sx={{ fontSize: "1.1rem", maxWidth: 600, mb: 5 }}>{t("courses.subtitle")}</Typography>

        {assignments.length > 0 && (
          <Box sx={{ mb: 5, p: { xs: 3, sm: 3.5 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <RecommendRoundedIcon sx={{ color: "primary.main" }} />
              <Typography variant="h3" sx={{ fontSize: "1.15rem", m: 0, color: "primary.dark" }}>{t("assign.clientTitle")}</Typography>
            </Box>
            <Stack spacing={1.25}>
              {assignments.map((a) => (
                <Box key={a.id} component={RouterLink}
                  to={a.kind === "selftest" ? `/zelfhulp/test/${a.selfTestSlug}` : `/zelfhulp/${a.courseSlug}`}
                  sx={{ textDecoration: "none", color: "text.primary" }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {a.kind === "selftest" ? a.selfTestTitle : a.courseTitle}{a.moduleTitle ? ` · ${a.moduleTitle}` : ""}
                  </Typography>
                  {a.note && <Typography sx={{ fontSize: ".88rem", color: "text.secondary" }}>“{a.note}”</Typography>}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
          {openCourses.map((c) => (
            <Box key={c.slug} component={RouterLink} to={`/zelfhulp/${c.slug}`}
              sx={{ p: 3.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)", textDecoration: "none", color: "text.primary", transition: "border-color .2s, transform .1s", "&:hover": { borderColor: "var(--primary-300)" }, "&:active": { transform: "scale(0.995)" } }}>
              <Box sx={{ color: "secondary.dark", mb: 1.5, "& svg": { fontSize: 30 } }}><MenuBookRoundedIcon /></Box>
              <Typography variant="h3" sx={{ fontSize: "1.25rem", mb: 1 }}>{c.title}</Typography>
              <Typography sx={{ fontSize: ".95rem", mb: 2 }}>{c.summary}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("courses.moduleCount", { count: c.moduleCount })} · {t("courses.lessonCount", { count: c.lessonCount })} · ~{c.totalMinutes} {t("courses.min")}</Typography>
            </Box>
          ))}
        </Box>
        {courses.length === 0 && <Typography sx={{ color: "text.secondary" }}>{t("courses.empty")}</Typography>}

        {assignableCourses.length > 0 && (
          <Box sx={{ mt: { xs: 7, sm: 10 } }}>
            <Typography variant="h2" sx={{ fontSize: { xs: "1.6rem", sm: "2rem" }, mb: 1 }}>{t("courses.assignableTitle")}</Typography>
            <Typography sx={{ fontSize: "1.05rem", maxWidth: 680, mb: 4, color: "text.secondary" }}>{t("courses.assignableIntro")}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
              {assignableCourses.map((c) => {
                const assigned = assignedSlugs.has(c.slug);
                const card = (
                  <>
                    <Box sx={{ color: "secondary.dark", mb: 1.5, "& svg": { fontSize: 30 } }}><WorkspacePremiumRoundedIcon /></Box>
                    <Typography variant="h3" sx={{ fontSize: "1.25rem", mb: 1 }}>{c.title}</Typography>
                    <Typography sx={{ fontSize: ".95rem", mb: 2 }}>{c.summary}</Typography>
                    <Typography variant="caption" sx={{ color: assigned ? "primary.dark" : "text.secondary", fontWeight: assigned ? 600 : 400 }}>
                      {assigned ? t("courses.assignableAssigned") : t("courses.assignableBadge")}
                    </Typography>
                  </>
                );
                const cardSx = { p: 3.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)", color: "text.primary" } as const;
                return assigned ? (
                  <Box key={c.slug} component={RouterLink} to={`/zelfhulp/${c.slug}`}
                    sx={{ ...cardSx, textDecoration: "none", transition: "border-color .2s, transform .1s", "&:hover": { borderColor: "var(--primary-300)" }, "&:active": { transform: "scale(0.995)" } }}>
                    {card}
                  </Box>
                ) : (
                  <Box key={c.slug} sx={cardSx}>{card}</Box>
                );
              })}
            </Box>
          </Box>
        )}

        {openTests.length > 0 && (
          <Box sx={{ mt: { xs: 7, sm: 10 } }}>
            <Typography variant="h2" sx={{ fontSize: { xs: "1.6rem", sm: "2rem" }, mb: 1 }}>{t("selftest.sectionTitle")}</Typography>
            <Typography sx={{ fontSize: "1.05rem", maxWidth: 600, mb: 4, color: "text.secondary" }}>{t("selftest.sectionSubtitle")}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
              {openTests.map((s) => (
                <Box key={s.slug} component={RouterLink} to={`/zelfhulp/test/${s.slug}`}
                  sx={{ p: 3.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)", textDecoration: "none", color: "text.primary", transition: "border-color .2s, transform .1s", "&:hover": { borderColor: "var(--primary-300)" }, "&:active": { transform: "scale(0.995)" } }}>
                  <Box sx={{ color: "primary.main", mb: 1.5, "& svg": { fontSize: 30 } }}><FactCheckRoundedIcon /></Box>
                  <Typography variant="h3" sx={{ fontSize: "1.25rem", mb: 1 }}>{s.title}</Typography>
                  <Typography sx={{ fontSize: ".95rem", mb: 2 }}>{s.intro}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("selftest.questionCount", { count: s.questionCount })}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Reflectie-vragenlijsten (visibility "assignable"): alleen naam +
            beschrijving; invullen kan pas na toewijzing door de psycholoog
            (API-afgedwongen). Antwoorden blijven sowieso client-side. */}
        {assignableTests.length > 0 && (
          <Box sx={{ mt: { xs: 7, sm: 10 } }}>
            <Typography variant="h2" sx={{ fontSize: { xs: "1.6rem", sm: "2rem" }, mb: 1 }}>{t("selftest.assignableTitle")}</Typography>
            <Typography sx={{ fontSize: "1.05rem", maxWidth: 680, mb: 4, color: "text.secondary" }}>{t("selftest.assignableIntro")}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
              {assignableTests.map((s) => {
                const assigned = assignedTestSlugs.has(s.slug);
                const card = (
                  <>
                    <Box sx={{ color: "primary.main", mb: 1.5, "& svg": { fontSize: 30 } }}><FactCheckRoundedIcon /></Box>
                    <Typography variant="h3" sx={{ fontSize: "1.25rem", mb: 1 }}>{s.title}</Typography>
                    <Typography sx={{ fontSize: ".95rem", mb: 2 }}>{s.intro}</Typography>
                    <Typography variant="caption" sx={{ color: assigned ? "primary.dark" : "text.secondary", fontWeight: assigned ? 600 : 400 }}>
                      {assigned ? t("selftest.assignableAssigned") : t("selftest.assignableBadge")}
                    </Typography>
                  </>
                );
                const cardSx = { p: 3.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)", color: "text.primary" } as const;
                return assigned ? (
                  <Box key={s.slug} component={RouterLink} to={`/zelfhulp/test/${s.slug}`}
                    sx={{ ...cardSx, textDecoration: "none", transition: "border-color .2s, transform .1s", "&:hover": { borderColor: "var(--primary-300)" }, "&:active": { transform: "scale(0.995)" } }}>
                    {card}
                  </Box>
                ) : (
                  <Box key={s.slug} sx={cardSx}>{card}</Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}
